// Easing functions for animations
export type EasingFunction = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut';

// Animation keyframe
export interface Keyframe {
  at: number;
  value: number;
  easing?: EasingFunction;
}

// Animation trigger
export interface AnimationTrigger {
  event: 'playbackPosition';
  time: number;
}

// Animation definition
export interface Animation {
  property: 'opacity' | 'scale' | 'rotation' | 'translateX' | 'translateY';
  trigger?: AnimationTrigger;
  keyframes: Keyframe[];
  loop?: boolean;
}

// Available font families
export type FontFamily =
  | 'Host Grotesk'
  | 'ChicagoFLF'
  | 'IBM Plex Mono'
  | 'Instrument Serif'
  | 'Inter'
  | 'Michroma'
  | 'Oswald'
  | 'Playfair Display'
  | 'Space Grotesk'
  | 'VT323'
  | 'Xanh Mono';

// Element style properties
export interface ElementStyle {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  opacity?: number;
  scale?: number;
  rotation?: number;
  translateX?: number;
  translateY?: number;
  backgroundColor?: string;
  borderRadius?: number;
  color?: string;
  fontSize?: number;
  fontFamily?: FontFamily;
  fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: number;
  padding?: number;
  marquee?: boolean;
  visible?: boolean;
}

// Per-element keyframe for time-based style/text changes
export interface ElementKeyframe {
  time: number;              // Time in seconds
  style?: Partial<ElementStyle>;  // Style overrides (sparse - only changed properties)
  text?: string;             // Text content override (for text elements)
  interpolate?: boolean;     // If true, tween from previous keyframe
  easing?: EasingFunction;   // Easing function (default: linear)
}

// Legacy text keyframe (deprecated - use per-element keyframes instead)
export interface TextKeyframe {
  trackId: number;        // Which track this applies to
  keyframeIndex: number;  // Which global keyframe this corresponds to
  text?: string;          // Text override
  style?: Partial<ElementStyle>;  // Style overrides
}

// Visibility trigger - conditions for when an element is visible
export interface VisibilityTrigger {
  type: 'variable';
  variableName: string;
  value: string;  // The value the variable must equal for this element to be visible
}

// State condition - when a state is active
export interface StateCondition {
  type?: 'variable' | 'trackStart';  // Condition type (default: 'variable')
  variable?: string;   // Variable name (for type: 'variable')
  value?: string;      // Required value (for type: 'variable')
  trackId?: number;    // Track ID (for type: 'trackStart')
}

// Element state - a set of overrides that apply when conditions are met
export interface ElementState {
  id: string;              // Unique identifier for this state
  conditions: StateCondition[];  // All must be satisfied (AND logic)
  // Property overrides when this state is active
  text?: string;           // For text elements
  style?: Partial<ElementStyle>;  // Style overrides
}

// Base element
interface BaseElement {
  id: string;
  style?: ElementStyle;
  keyframes?: { [trackId: number]: ElementKeyframe[] };  // Per-track keyframes
  animations?: Animation[];
  visibilityTriggers?: VisibilityTrigger[];  // Keep for backwards compat
  textKeyframes?: TextKeyframe[];             // Keep for backwards compat (will migrate)
  states?: ElementState[];                    // Variable-based visibility only
}

// Element types
export interface ImageElement extends BaseElement {
  type: 'image';
  src: string;
}

export interface ViewElement extends BaseElement {
  type: 'view';
  children?: ExperienceElement[];
}

export interface TextElement extends BaseElement {
  type: 'text';
  text: string;                    // Default/initial text
}

export interface VideoElement extends BaseElement {
  type: 'video';
  src: string;
  muted?: boolean;
  loop?: boolean;
}

// Button action types
export interface NavigateToLayoutAction {
  type: 'navigateToLayout';
  targetLayout: string;
}

export interface SetVariableAction {
  type: 'setVariable';
  variableName: string;
  value: string;
}

export type ButtonAction = NavigateToLayoutAction | SetVariableAction;

export interface ButtonElement extends BaseElement {
  type: 'button';
  text: string;
  actions?: ButtonAction[];  // Multiple actions executed in order
  // Legacy single action fields (for backwards compatibility)
  action?: 'navigateToLayout' | 'setVariable';
  targetLayout?: string;
  targetVariable?: string;
  variableValue?: string;
}

export type ExperienceElement = ImageElement | ViewElement | TextElement | VideoElement | ButtonElement;

// Canvas definition
export interface Canvas {
  width: number;
  height: number;
  backgroundColor?: string;
}

// Layout trigger types
export type LayoutTriggerEvent = 'trackStart' | 'playbackPosition';

export interface LayoutTrigger {
  event: LayoutTriggerEvent;
  trackId?: number;    // For 'trackStart' - which track triggers this layout
  time?: number;       // For 'playbackPosition' - time in seconds
}

// Layout
export interface Layout {
  canvas: Canvas;
  elements: ExperienceElement[];
  trigger?: LayoutTrigger;  // How this layout is activated (default layout has no trigger)
}

/** @deprecated - for migration only */
export interface TimelineKeyframe {
  time: number;
  interpolate?: boolean;
  easing?: EasingFunction;
}

/** @deprecated - for migration only */
export interface TimelineKeyframes {
  [trackId: number]: TimelineKeyframe[];
}

// Variable definition
export interface VariableDefinition {
  name: string;
  defaultValue: string;
}

// Top-level experience JSON
export interface ExperienceJSON {
  version: string;
  layouts: {
    default: Layout;
    [key: string]: Layout;
  };
  variables?: VariableDefinition[];
  // timelineKeyframes removed - keyframes now live on elements
}

// Default empty experience
export const createEmptyExperience = (): ExperienceJSON => ({
  version: '1.0',
  layouts: {
    default: {
      canvas: {
        width: 375,
        height: 875,
        backgroundColor: '#FFFFFF',
      },
      elements: [],
    },
  },
});
